import { Skeleton } from '@tiger/bs-comps';
import { useStates } from '@tiger/hooks';
import { useSWR } from '@tiger/swr';
import { FC, useEffect } from 'react';
import { FormControl, FormGroup as FormGroupBase, FormLabel } from 'react-bootstrap';
import { connect } from 'react-bootstrap-formutil';
import { getToday } from 'components/forms/AgreementInfo';
import Checkbox from 'components/forms/Checkbox';
import { getAddressDetail } from 'components/forms/HomeAddress';
import { getFullName } from 'components/forms/RealNameInfo';
import Toast from 'components/Toast';
import { CountryItem } from 'types/common';
import { IdTypeEnum } from 'types/customer';
import API from 'utils/API';
import classlist from 'utils/classlist';
import { printf } from 'utils/i18n';
import Signature from '../Signature';
import { getDeliveryAddressDetail, proveContentList } from './helper';
import { W8benProps, W8benState } from './type';
import './style.scss';

/**
 *  非SSN的 w8 目前全得是英文文案
 */
const W8ben: FC<W8benProps> = ({ customerInfo, className, disabled, useHomeAddress }) => {
    const { birthday, tax_identification_number, id_no, id_type } = customerInfo;
    const isSSN = id_type === IdTypeEnum.SSN_IDS;
    const [{ countryCode, taxCountry, legalResidenceCountry, deliveryCountry }, setStates] = useStates<
        Partial<W8benState>,
        {}
    >({});
    const { data = [], isLoading } = useSWR<CountryItem[]>(
        {
            url: isSSN ? API.CUSTOMER.fields.countryUS() : API.INFOBASE.getCountryList('country_non_ssn'),
            params: {
                lang: 'en_US'
            }
        },
        {
            immutable: true,
            dataChain: res => res.data,
            onError: err => Toast.show(err)
        }
    );

    useEffect(() => {
        // W8页面所展示的国家信息, 均从全部接口中映射
        if (data.length > 0) {
            const { country, tax_residence_country, legal_residence_country, delivery_country } = customerInfo;
            const countryItem = data.find(item => item.country_code === country);
            const taxCountryItem = data.find(item => item.country_code === tax_residence_country);
            const legalResCountryItem = data.find(item => item.country_code === legal_residence_country); // 居住国
            const deliveryCountryItem = data.find(
                item => item.country_code === (useHomeAddress ? legal_residence_country : delivery_country)
            );

            setStates({
                countryCode: countryItem,
                taxCountry: taxCountryItem,
                legalResidenceCountry: legalResCountryItem,
                deliveryCountry: deliveryCountryItem
            });
        }
    }, [data, customerInfo, setStates, useHomeAddress]);

    if (isLoading) {
        return <Skeleton line={5} />;
    }

    return (
        <div className={classlist('w8ben-root', className)}>
            <div className="taxform-component-root">
                <h3
                    className="text-center"
                    dangerouslySetInnerHTML={{
                        __html: 'W-8BEN Substitute\nCertificate of Foreign Status of Beneficial Owner for\nUnited States Tax Withholding and Reporting (Individuals)'
                    }}
                />
                <p
                    dangerouslySetInnerHTML={{
                        __html: 'Do NOT use this form if:\n1.You are NOT an individual, please use form W-8BEN-E.\n2.You are a U.S. citizen or other U.S. person, including a resident alien individual,please use form W-9\n3.You are a beneficial owner claiming that income is effectively connected with the conduct of trade or business within the U.S. (other than personal services),please use form W-8ECI.\n4.You are a beneficial owner who is receiving compensation for personal services performed in the United States, please use form 8233 or form W-4.\n5.A person acting as an intermediary,please use form W-8IMY.'
                    }}
                />
                <p
                    dangerouslySetInnerHTML={{
                        __html: 'Please note that if you live in a FATCA co-operating jurisdiction, such as an IGA jurisdiction with reciprocity policy type 1). Your tax account information may be provided to the jurisdiction where you live.'
                    }}
                />
                {/* 受益所有人身份 */}
                <div className="w8ben-benefit">
                    <h4>Part I: Identification of Beneficial Owner</h4>
                    <FormGroupBase className="mb-3">
                        <FormLabel>Name of individual who is the beneficial owner</FormLabel>
                        <FormControl disabled value={getFullName(customerInfo) || ''} />
                    </FormGroupBase>
                    <FormGroupBase className="mb-3">
                        <FormLabel>Country of citizenship</FormLabel>
                        <FormControl disabled value={countryCode?.country || ''} />
                    </FormGroupBase>
                    <FormGroupBase className="mb-3">
                        <FormLabel>Permanent residence address</FormLabel>
                        <FormGroupBase className="mb-3">
                            <FormControl disabled defaultValue={getAddressDetail(customerInfo)} />
                        </FormGroupBase>
                        <FormControl disabled value={legalResidenceCountry?.country || ''} />
                    </FormGroupBase>
                    <FormGroupBase className="mb-3">
                        <FormLabel>Mailing address</FormLabel>
                        <FormGroupBase className="mb-3">
                            <FormControl
                                disabled
                                defaultValue={getDeliveryAddressDetail(customerInfo, !!useHomeAddress)}
                            />
                        </FormGroupBase>
                        <FormControl disabled value={deliveryCountry?.country || customerInfo.delivery_country || ''} />
                    </FormGroupBase>
                    <FormGroupBase className="mb-3">
                        <FormLabel>Foreign tax identifying number</FormLabel>
                        <FormControl disabled value={tax_identification_number || id_no || ''} />
                    </FormGroupBase>
                    <FormGroupBase className="mb-3">
                        <FormLabel>Date of birth</FormLabel>
                        <FormControl disabled value={birthday} />
                    </FormGroupBase>
                </div>
                <div className="w8ben-tax-country">
                    <h4>Part II:Claim of Tax Treaty Benefits</h4>
                    {printf(
                        'I certify that the beneficial owner is a resident of %s within the meaning of the income tax treaty between the United States and that country.',
                        taxCountry?.country || ''
                    )}
                </div>
                {/* 证明 */}
                <div className="prove-info">
                    <h4>Part III: Certification</h4>
                    <p>
                        Under penalties of perjury, I declare that I have examined the information on this form and to
                        the best of my knowledge and belief it is true, correct, and complete. I further certify under
                        penalties of perjury that:
                    </p>
                    {proveContentList.map((item, index) => (
                        <dl className="dl-horizontal my-2" key={index}>
                            <dt>
                                <input type="checkbox" disabled defaultChecked />
                            </dt>
                            {index === 2 ? (
                                <dd
                                    dangerouslySetInnerHTML={{
                                        __html: "The income to which this form relates is:\n(a)not effectively connected with the conduct of a trade or business in the United States,\n(b)effectively connected but is not subject to tax under an income tax treaty, or\n(c)the partner's share of a partnership's effectively connected income,"
                                    }}
                                />
                            ) : (
                                <dd dangerouslySetInnerHTML={{ __html: item }} />
                            )}
                        </dl>
                    ))}
                </div>
            </div>
            <Checkbox
                hideError={false}
                name="temporary_w8_confirm_signature"
                label="I certify that I have the capacity to sign for the person identified on line 1 of this form"
                $defaultValue={true}
                checker={value => !!value || 'You must agree to this before you can continue to open an account'}
                disabled={disabled}
            />
            <Signature name={getFullName(customerInfo)} date={getToday(true)} />
        </div>
    );
};

export default connect(W8ben);
export * from './type';
